/**
 * Eight techniques, because eight done properly beats fifty as a list.
 *
 * These are the ones that actually separate food that works from food that
 * doesn't, and every one of them is invisible in a normal recipe — a recipe
 * says "sear the chicken" and assumes you know what that means, which is
 * exactly the gap this app kept leaving.
 *
 * Six can be judged from a photo. **Two cannot**, and they say so. Seasoning in
 * layers and resting meat leave no reliable visual trace, and a model asked to
 * grade them from a picture would invent an answer with total confidence — the
 * single worst thing a teaching feature can do to a beginner.
 */

import type { Technique } from "./types";

export const TECHNIQUES: Technique[] = [
  {
    id: "even-dice",
    name: "An even dice",
    summary: "Same-sized pieces, so everything finishes at the same time.",
    why: "This is the one that makes the biggest difference to the least glamorous thing: whether your food is cooked. Pieces of different sizes finish at different times, so by the time the big ones are soft the small ones have burnt. Every 'why did some of my onion go bitter' has this answer.",
    steps: [
      "Cut a thin slice off one side so it sits flat and can't roll.",
      "Slice it into planks of the thickness you want the final cubes to be.",
      "Stack a couple of planks, cut into sticks of the same thickness.",
      "Turn ninety degrees and cut across the sticks.",
    ],
    cues: [
      "The pieces look boring and identical. Boring is correct.",
      "Nothing rolls away from the knife — the flat side is doing its job.",
      "You can see through the pile to the board; they aren't crushed.",
    ],
    mistakes: [
      "Skipping the flat side, so it rocks and every cut is a different thickness.",
      "A blunt knife, which crushes rather than cuts and makes onion sting.",
      "Chasing speed. Even and slow beats fast and ragged every single time.",
    ],
    check: {
      shoot: "Spread your chopped pieces on the board and photograph them from above.",
      looksFor:
        "diced vegetable pieces on a board, judged on whether the pieces are a consistent size and shape",
    },
    level: "beginner",
  },
  {
    id: "hot-pan-sear",
    name: "A proper sear",
    summary: "Real browning, which is flavour you cannot add later.",
    why: "Browning isn't cosmetic. It's a chemical reaction that creates hundreds of flavour compounds that were not in the raw ingredient, and there is no bottle you can add afterwards that replaces it. Grey meat tastes of nothing because nothing happened to it.",
    steps: [
      "Dry the surface with kitchen paper. Twice, if it's been in a marinade.",
      "Heat the pan empty until a drop of water skitters rather than sits.",
      "Oil, then the food, laid away from you.",
      "Leave it completely alone for two to three minutes.",
      "It releases by itself when the crust forms. Then, and only then, turn it.",
    ],
    cues: [
      "It sticks at first and unsticks on its own. That's the crust forming.",
      "You hear a steady sizzle, not a hiss that dies away.",
      "The underside is properly brown, not beige.",
    ],
    mistakes: [
      "A wet surface. Water has to boil off before browning can start, and by then the inside is overcooked.",
      "Moving it to 'check'. Every poke tears the crust that was forming.",
      "A pan that isn't hot enough, which steams the food in its own juice.",
    ],
    check: {
      shoot: "Photograph the browned side, in decent light, right after you turn it.",
      looksFor:
        "the cooked surface of seared food, judged on whether there is genuine deep browning rather than pale or grey cooking",
    },
    level: "beginner",
  },
  {
    id: "dont-crowd",
    name: "Don't crowd the pan",
    summary: "Space between things, or they steam instead of roasting.",
    why: "Food gives off water as it heats. With room, that steam escapes and the surface can get above 100°C, which is where browning happens. Packed in, the steam has nowhere to go, the temperature stalls, and you get pale soggy food no matter how long you leave it or how hot the oven is.",
    steps: [
      "Spread everything in a single layer.",
      "Leave a finger's width between pieces.",
      "If it doesn't fit, use two trays. It is genuinely faster than one crowded one.",
    ],
    cues: [
      "You can see tray between the pieces.",
      "Everything is touching the tray, nothing is sitting on anything else.",
      "Twenty minutes in, the edges are colouring rather than glistening.",
    ],
    mistakes: [
      "Doubling a recipe onto the same tray.",
      "Two layers 'because it'll shrink'. It will, but only after it has steamed.",
      "Blaming the oven.",
    ],
    check: {
      shoot: "Photograph the tray or pan from above, before it goes in the heat.",
      looksFor:
        "food arranged on a tray or in a pan, judged on whether the pieces are in a single layer with visible space between them",
    },
    level: "beginner",
  },
  {
    id: "knife-grip",
    name: "Holding the knife",
    summary: "Pinch the blade, claw the other hand. Faster and much safer.",
    why: "Almost every beginner holds a knife like a hammer and holds the food with flat fingers. Both are slower and both are how people cut themselves. The pinch grip gives you control from the wrist rather than the elbow; the claw makes it physically difficult to cut your fingertips, because they aren't the closest thing to the blade.",
    steps: [
      "Pinch the blade between thumb and the side of your index finger, just past the handle.",
      "Wrap the other three fingers around the handle.",
      "Curl the fingertips of your other hand under, knuckles forward.",
      "Rest the flat of the blade against those knuckles and let them guide it.",
    ],
    cues: [
      "The knife feels like an extension of your forearm, not a tool you're wielding.",
      "Your guiding knuckles touch the blade and your fingertips never do.",
      "You move the guiding hand back between cuts; the knife stays put.",
    ],
    mistakes: [
      "Gripping the handle only — you lose all fine control.",
      "Flat fingers on the food, with fingertips level with the blade.",
      "Watching the knife instead of the guiding hand.",
    ],
    check: {
      shoot: "Get someone to photograph your hands mid-cut, or prop your phone up.",
      looksFor:
        "a person's hands holding a kitchen knife and guiding food, judged on whether the blade is pinched rather than gripped like a hammer and whether the guiding fingertips are curled under",
    },
    level: "beginner",
  },
  {
    id: "reduce",
    name: "Reducing a sauce",
    summary: "Boiling off water to concentrate what's left.",
    why: "A thin sauce isn't under-seasoned, it's under-reduced — and adding more salt to fix it makes it salty and still thin. Driving off water concentrates everything that was already there. It's the difference between a pan of liquid and something that coats what you pour it over.",
    steps: [
      "Take the lid off. A lid returns the steam you're trying to lose.",
      "Simmer briskly. Not a violent boil, which can split a sauce with dairy in it.",
      "Drag a spoon through it now and then to see the bottom.",
      "Season at the end. Seasoning first means seasoning the water you removed.",
    ],
    cues: [
      "A spoon dragged across the pan leaves a channel that holds for a second.",
      "The colour deepens noticeably.",
      "It coats the back of a spoon instead of running off it.",
    ],
    mistakes: [
      "Salting early, then finding it inedible by the end.",
      "Leaving the lid on and wondering why nothing is happening.",
      "Walking away — the last third goes much faster than the first.",
    ],
    check: {
      shoot: "Drag a spoon through the pan and photograph the channel it leaves.",
      looksFor:
        "a sauce in a pan with a spoon drawn through it, judged on whether the sauce is thick enough to briefly hold the channel rather than running straight back",
    },
    level: "intermediate",
  },
  {
    id: "emulsify",
    name: "Making a sauce cling",
    summary: "Starchy water plus fat, agitated, becomes a sauce.",
    why: "This is why restaurant pasta tastes different from yours. Fat and water don't mix on their own — they separate and slide to the bottom of the bowl. Starch from the pasta water holds them together, so what was oil and liquid becomes one glossy thing that clings to every strand.",
    steps: [
      "Keep a mug of the pasta water before draining. It looks like nothing; it is the ingredient.",
      "Finish the pasta in the pan with the fat, a minute short of done.",
      "Add water a splash at a time, moving the pan constantly.",
      "Keep going until it turns glossy rather than oily.",
    ],
    cues: [
      "The sauce goes from separated and greasy to one creamy colour.",
      "It clings to the pasta and leaves the pan almost clean.",
      "The bottom of the bowl stays dry when you serve it.",
    ],
    mistakes: [
      "Draining pasta water away. Nearly everyone does this once.",
      "Adding all the water at once, which just makes soup.",
      "Not moving the pan. The agitation is doing half the work.",
    ],
    check: {
      shoot: "Photograph the finished pasta in the pan, tilted slightly toward you.",
      looksFor:
        "finished pasta in a pan, judged on whether the sauce looks glossy and clinging rather than oily and pooling separately",
    },
    level: "intermediate",
  },
  {
    id: "season-in-layers",
    name: "Seasoning in layers",
    summary: "A little at each stage, not all of it at the end.",
    why: "Salt added at the end sits on the surface and tastes like salt. Salt added while something cooks dissolves into it and tastes like the food, more so. The same total amount, added in three goes, produces something that tastes seasoned rather than salted — and you almost always end up using less.",
    steps: [
      "A pinch when the onions go in.",
      "A pinch when the main ingredient joins them.",
      "Taste before serving, and adjust then.",
      "Something acidic at the very end — lemon or vinegar — if it still tastes flat.",
    ],
    cues: [
      "You reach for the salt at the table less often.",
      "Flat-tasting food usually wants acid, not more salt. Try that first.",
    ],
    mistakes: [
      "All the salt at the end.",
      "Salting a sauce before reducing it, then finding it inedible.",
      "Never tasting while you cook. It's the only feedback there is.",
    ],
    level: "beginner",
  },
  {
    id: "rest-meat",
    name: "Resting meat",
    summary: "Wait, and the juice stays in it instead of on the board.",
    why: "Heat drives moisture toward the middle and tightens the muscle fibres holding it. Cut into it straight away and that liquid runs out onto the board — that puddle is what your steak was supposed to taste like. Waiting lets the fibres relax and reabsorb it.",
    steps: [
      "Take it off the heat and put it somewhere warm.",
      "Rest roughly as long as it cooked, up to about ten minutes for a steak.",
      "Don't cover it tightly — trapped steam softens any crust you built.",
      "Pour any juice that does escape back over it.",
    ],
    cues: [
      "Almost no liquid on the board when you slice.",
      "The colour has evened out rather than being a ring around a raw middle.",
    ],
    mistakes: [
      "Cutting in immediately to check it. Use a thermometer instead.",
      "Wrapping it in foil, which steams the crust soft.",
      "Resting so long it goes cold. It carries on cooking; plan for that.",
    ],
    level: "beginner",
  },
];

export function findTechnique(id: string): Technique | null {
  return TECHNIQUES.find((technique) => technique.id === id) ?? null;
}
